import React, { useEffect, useMemo, useState } from "react";
import {
    Modal,
    Box,
    Typography,
    Grid,
    Button,
    TextField,
    Checkbox,
    FormControlLabel,
} from "@mui/material";
import { CustomDatePicker } from "../../../components/Custom/CustomDatePicker";
import {
    DepartmentSelect,
    FilialSelect,
    FilialDivisionSelect2,
    PeriodicitySelect,
} from "../../../components/Select/domainSelects";

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 600,
    maxHeight: "90vh",
    overflowY: "auto",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
};

const toNullableNumber = (v) => {
    if (v === "" || v === undefined || v === null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

const EquipmentIndicatorCreateModal = ({
    open,
    onClose,
    onSubmit,
    initialData,
    mode = "create", // create | edit | details
}) => {
    const isReadOnly = mode === "details";

    const emptyForm = useMemo(
        () => ({
            id: null,
            filialId: "",
            filialDivisionId: "",
            departmentId: "",
            name: "",
            type: "",
            serialNumber: "",
            manufactureYear: "",
            quantity: 1,
            rankAccuracyError: "",
            measurementRange: "",
            note: "",
            periodicity: "",
            lastVerificationDate: null,
            nextVerificationDate: null,
            isIrrelevant: false,
            state: 1,
        }),
        []
    );

    const [formData, setFormData] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    const handleChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleFilialChange = (value) => {
        setFormData((prev) => ({
            ...prev,
            filialId: value,
            filialDivisionId: prev.filialDivisionId && Number(prev.filialId) !== Number(value) ? "" : prev.filialDivisionId,
        }));
    };

    const handleFilialDivisionChange = (value, filialDivision) => {
        setFormData((prev) => ({
            ...prev,
            filialDivisionId: value,
            filialId:
                filialDivision != null &&
                Number(filialDivision.filialId) !== Number(prev.filialId)
                    ? filialDivision.filialId
                    : prev.filialId,
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!onSubmit) {
            onClose();
            return;
        }
        setSaving(true);
        Promise.resolve(
            onSubmit({
                ...formData,
                id: toNullableNumber(formData.id),
                filialId: toNullableNumber(formData.filialId),
                filialDivisionId: toNullableNumber(formData.filialDivisionId),
                departmentId: toNullableNumber(formData.departmentId),
                quantity: Number.isFinite(Number(formData.quantity)) ? Number(formData.quantity) : 1,
            })
        )
            .then(() => handleClose())
            .catch((err) => alert(err))
            .finally(() => setSaving(false));
    };

    const handleClose = () => {
        setFormData(emptyForm);
        onClose();
    };

    useEffect(() => {
        if (!open) return;
        if (!initialData) {
            setFormData(emptyForm);
            return;
        }
        setFormData((prev) => ({
            ...prev,
            id: initialData.id ?? null,
            filialId: initialData.filialId ?? "",
            filialDivisionId: initialData.filialDivisionId ?? "",
            departmentId: initialData.departmentId ?? "",
            name: initialData.name ?? "",
            type: initialData.type ?? "",
            serialNumber: initialData.serialNumber ?? "",
            manufactureYear: initialData.manufactureYear ?? "",
            quantity: Number.isFinite(Number(initialData.quantity)) ? Number(initialData.quantity) : 1,
            rankAccuracyError: initialData.rankAccuracyError ?? "",
            measurementRange: initialData.measurementRange ?? "",
            note: initialData.note ?? "",
            periodicity: initialData.periodicity ?? "",
            lastVerificationDate: initialData.lastVerificationDate
                ? new Date(initialData.lastVerificationDate)
                : null,
            nextVerificationDate: initialData.nextVerificationDate
                ? new Date(initialData.nextVerificationDate)
                : null,
            isIrrelevant: Boolean(initialData.isIrrelevant),
            state: initialData.state ?? 1,
        }));
    }, [open, initialData, emptyForm]);

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box sx={style}>
                <Typography id="modal-modal-title" variant="h6" component="h2" sx={{ mb: 3 }}>
                    {mode === "edit"
                        ? "Редактирование индикатора поверки"
                        : mode === "details"
                        ? "Просмотр индикатора поверки"
                        : "Индикатор по поверке оборудования"}
                </Typography>
                <Box component="form" onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FilialSelect
                                label="Филиал"
                                fullWidth
                                value={formData.filialId}
                                onChange={handleFilialChange}
                                disabled={isReadOnly}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FilialDivisionSelect2
                                label="Районное отделение"
                                fullWidth
                                value={formData.filialDivisionId}
                                filialId={formData.filialId}
                                onChange={handleFilialDivisionChange}
                                disabled={isReadOnly}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <DepartmentSelect
                                label="Лаборатория"
                                fullWidth
                                value={formData.departmentId}
                                onChange={(value) => handleChange("departmentId", value)}
                                disabled={isReadOnly}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                margin="normal"
                                fullWidth
                                label="Наименование"
                                value={formData.name}
                                onChange={(e) => handleChange("name", e.target.value)}
                                disabled={isReadOnly}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                margin="normal"
                                fullWidth
                                label="Тип"
                                value={formData.type}
                                onChange={(e) => handleChange("type", e.target.value)}
                                disabled={isReadOnly}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                margin="normal"
                                fullWidth
                                label="Заводской номер"
                                value={formData.serialNumber}
                                onChange={(e) => handleChange("serialNumber", e.target.value)}
                                disabled={isReadOnly}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                margin="normal"
                                fullWidth
                                label="Год выпуска"
                                value={formData.manufactureYear}
                                onChange={(e) => handleChange("manufactureYear", e.target.value)}
                                disabled={isReadOnly}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                margin="normal"
                                fullWidth
                                label="Количество"
                                type="number"
                                inputProps={{ min: 1, step: 1 }}
                                value={formData.quantity}
                                onChange={(e) => handleChange("quantity", e.target.value)}
                                disabled={isReadOnly}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                margin="normal"
                                fullWidth
                                label="Разряд, класс точности, погрешность"
                                value={formData.rankAccuracyError}
                                onChange={(e) => handleChange("rankAccuracyError", e.target.value)}
                                disabled={isReadOnly}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                margin="normal"
                                fullWidth
                                label="Диапазон измерения"
                                value={formData.measurementRange}
                                onChange={(e) => handleChange("measurementRange", e.target.value)}
                                disabled={isReadOnly}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <PeriodicitySelect
                                value={formData.periodicity}
                                onChange={(value) => handleChange("periodicity", value)}
                                disabled={isReadOnly}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                margin="normal"
                                fullWidth
                                label="Примечание"
                                value={formData.note}
                                onChange={(e) => handleChange("note", e.target.value)}
                                disabled={isReadOnly}
                                multiline
                                minRows={2}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <CustomDatePicker
                                label="Дата последней поверки"
                                value={formData.lastVerificationDate}
                                onChange={(value) => handleChange("lastVerificationDate", value)}
                                autoFormatDDMMYYYY
                                fullWidth
                                disabled={isReadOnly}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <CustomDatePicker
                                label="Дата последующей поверки"
                                value={formData.nextVerificationDate}
                                onChange={(value) => handleChange("nextVerificationDate", value)}
                                autoFormatDDMMYYYY
                                fullWidth
                                disabled={isReadOnly}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.isIrrelevant}
                                        onChange={(e) => handleChange("isIrrelevant", e.target.checked)}
                                        disabled={isReadOnly}
                                    />
                                }
                                label="Неактуальный"
                            />
                        </Grid>
                        {mode !== "details" && (
                            <Grid item xs={12}>
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    sx={{ mt: 2, mb: 2 }}
                                    disabled={saving}
                                >
                                    Сохранить
                                </Button>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </Box>
        </Modal>
    );
};

export default EquipmentIndicatorCreateModal;

